import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff, DragIndicator } from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import UniversityLogo from '../UniversityCRMEntryForm/UniversityLogo';
import FormTextInput from '../UniversityCRMEntryForm/FormTextInput';
import FormSelectAutocomplete from '../UniversityCRMEntryForm/FormSelectAutocomplete';
import CampusImages from '../UniversityCRMEntryForm/CampusImages';
import AccreditationForm from '../UniversityCRMEntryForm/AccreditationForm';
import TextEditor from '../editor/TextEditor';
import SocialMediaLinks from '../UniversityCRMEntryForm/SocialMediaLinks';
import ReasonsToChooseUs from '../UniversityCRMEntryForm/ReasonsToChooseUs';
import FacultyTeam from '../UniversityCRMEntryForm/FacultyTeam';
import StudentSpotlight from '../UniversityCRMEntryForm/StudentSpotlight';
import AwardsForm from '../UniversityCRMEntryForm/AwardsForm';
import UploadBrochure from '../UniversityCRMEntryForm/UploadBrochure';
import UploadRelevantDocument from '../UniversityCRMEntryForm/UploadRelevantDocument';
import ChangeHistory from '../UniversityCRMEntryForm/ChangeHistory';
import { useDispatch, useSelector } from 'react-redux'
import { fetchUniversity, saveUniversity, updateUniversityStatus } from '../../api/universityApi'
import { useNotification } from '../../hooks/useNotification'
import { resetFields, setFields } from '../../containers/university-container/reducer/universityFormSlice.js'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Country, State, City } from 'country-state-city';
import { SortableItem } from '../CourseDetails/CourseDetailsForm.js';
import { updateStatus } from '../../api/adminApi.js';
import QuillEditor from '../editor/QuillEditor.js';
import DOMPurify from 'dompurify';
import { validateSchema } from '../../utils/validation/validationSchema.js';

const institutionTypeOptions = [
  'Govt University',
  'Private University',
  'Deemed University',
  'State University',
];

const LocationSelect = ({ type, field, fields, setFields, countryId = 'country', stateId = 'state',fieldError }) => {
  const safeFields = Array.isArray(fields) ? fields : [];

  const applyPatches = useCallback((patch) => {
    let changed = false;
    const next = safeFields.map(f => {
      if (Object.prototype.hasOwnProperty.call(patch, f.id)) {
        const newVal = patch[f.id];
        if (f.value !== newVal) {
          changed = true;
          return { ...f, value: newVal };
        }
      }
      return f;
    });
    if (changed) setFields(next);
  }, [safeFields, setFields]);

  const getVal = useCallback((id) => safeFields.find(f => f.id === id)?.value || '', [safeFields]);

  const countryName = getVal(countryId);
  const stateName = getVal(stateId);
  const cityName = type === 'city' ? field?.value || '' : getVal('city');
  const allCountries = useMemo(() => Country.getAllCountries(), []);
  const selectedCountry = useMemo(() => allCountries.find(c => c.name === countryName) || null, [allCountries, countryName]);
  const allStates = useMemo(() => selectedCountry ? State.getStatesOfCountry(selectedCountry.isoCode) : [], [selectedCountry]);
  const selectedState = useMemo(() => allStates.find(s => s.name === stateName) || null, [allStates, stateName]);
  const countryOptions = useMemo(() => allCountries.map(c => c.name), [allCountries]);
  const stateOptions = useMemo(() => allStates.map(s => s.name), [allStates]);
  const cityOptions = useMemo(
    () => (selectedCountry && selectedState)
      ? City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode).map(ci => ci.name)
      : [],
    [selectedCountry, selectedState]
  );

  const handleChange = useCallback((newVal) => {
    const v = typeof newVal === 'string' ? newVal : '';

    if (type === 'country') {
      const patch = { [countryId]: v };
      if (stateName !== '') patch[stateId] = '';
      const hasCity = safeFields.some(f => f.id === 'city');
      if (hasCity && cityName !== '') patch['city'] = '';
      applyPatches(patch);
    } else if (type === 'state') {
      const patch = { [stateId]: v };
      const hasCity = safeFields.some(f => f.id === 'city');
      if (hasCity && cityName !== '') patch['city'] = '';
      applyPatches(patch);
    } else {
      applyPatches({ [field.id]: v });
    }
  }, [type, countryId, stateId, field?.id, safeFields, stateName, cityName, applyPatches]);

  let options = countryOptions;
  let value = countryName;
  let disabled = field?.readOnly || false;

  if (type === 'state') {
    options = stateOptions;
    value = stateName;
    disabled = field?.readOnly || !countryName;
  } else if (type === 'city') {
    options = cityOptions;
    value = cityName;
    disabled = field?.readOnly || !countryName || !stateName;
  }

  return (
    <FormSelectAutocomplete
      label={field?.label || (type[0].toUpperCase() + type.slice(1))}
      value={value}
      options={options}
      readOnly={disabled}
      onChange={handleChange}
      fieldError={fieldError}
    />
  );
};

const UniversityDetails = ({ fields, setFields, fetchUniversity,editMode }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch()
  const location = useLocation();
  const isReadOnly = location.state?.viewOnly ?? false;
  const [fieldError,setFieldError] = useState(null)

  const universityState = useSelector((state) => state.universityForm)
  const { showSuccessNotification, showErrorNotification } = useNotification()
  // const fields = useSelector((state) => state.universityForm.fields)
  const { universityId } = useParams()

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  const setFieldState = (updatedFields) => setFields(updatedFields)
  
  // const [countryOptions, setCountryOptions] = useState([]);
  // const [stateOptions, setStateOptions] = useState([]);
  // const [cityOptions, setCityOptions] = useState([]);
  
  const runValidation = async (type) => {
    const errors = await validateSchema(fields, "university");
    setFieldError(errors);
    if(type=="submit" && Object.keys(errors?.errors).length===0){
      handleUniversitySubmit()
    }
  };
useEffect(() => {
  if(fieldError?.errors && Object.keys(fieldError.errors)?.length){
    runValidation();
  }
}, [fields]);

  //  useEffect(() => {
  //   setCountryOptions(
  //     Country.getAllCountries().map(c => ({
  //       label: c.name,
  //       value: c.isoCode,
  //     }))
  //   );
  // }, []);

  //  useEffect(() => {
  //   if (countryValue) {
  //     setStateOptions(
  //       State.getStatesOfCountry(countryValue).map(s => ({
  //         label: s.name,
  //         value: s.isoCode,
  //       }))
  //     );
  //   } else {
  //     setStateOptions([]);
  //   }
  //   // Optionally, clear state and city when country changes
  //   setFields(fields =>
  //     fields.map(f =>
  //       f.id === "state" || f.id === "city" ? { ...f, value: "" } : f
  //     )
  //   );
  //   // eslint-disable-next-line
  // }, [countryValue]);

  // // 5. Update city options when state changes
  // useEffect(() => {
  //   if (countryValue && stateValue) {
  //     setCityOptions(
  //       City.getCitiesOfState(countryValue, stateValue).map(c => ({
  //         label: c.name,
  //         value: c.name,
  //       }))
  //     );
  //   } else {
  //     setCityOptions([]);
  //   }
  //   // Optionally, clear city when state changes
  //   setFields(fields =>
  //     fields.map(f => (f.id === "city" ? { ...f, value: "" } : f))
  //   );
  //   // eslint-disable-next-line
  // }, [countryValue, stateValue]);

  //  const selectedCountry = countryOptions.find(opt => opt.value === countryValue) || null;
  // const selectedState = stateOptions.find(opt => opt.value === stateValue) || null;
  // const selectedCity = cityOptions.find(opt => opt.value === cityValue) || null;


  // useEffect(() => {
  //   const loadUniversity = async () => {
  //     try {
  //       const updatedFields = await fetchUniversity(universityId, fields)
  //       if (updatedFields) setFields(updatedFields)
  //     } catch (err) {
  //       console.error('Error fetching university:', err)
  //     }
  //   }

  //   if (universityId) {
  //     loadUniversity()
  //   } else {
  //     dispatch(resetFields())
  //   }
  // }, [universityId, dispatch])

  const handleDragEnd = (event) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = fields.findIndex(item => item.id === active.id)
    const newIndex = fields.findIndex(item => item.id === over.id)
    setFieldState(arrayMove(fields, oldIndex, newIndex))
  }

  const toggleVisibility = (id) => {
    setFieldState(fields.map(item =>
      item.id === id ? { ...item, visible: !item.visible } : item
    ))
  }

  function handleUniversitySubmit (){
    validateSchema(universityState.fields, "university")
    saveUniversity(universityState.fields)
      .then((data) =>
        fetchUniversity(data?.university?.universityId))
      .then(() => {
        showSuccessNotification('University saved successfully')
      })
      .catch((err) => {
        showErrorNotification(err.message || 'Failed to save university')
      })
  }

  const handleUniversityApproval = () => {
    const idField = universityState.fields.find(f => f.id === '_id')
    const objectId = idField?.value

    if (!objectId) {
      showErrorNotification('Please save university first')
      return
    }
    updateStatus({ id: objectId, status: "pending", type: "university" })
      .then(() => {
        showSuccessNotification('University sent for review successfully')
        navigate(-1)
      })
      .catch((err) => {
        showErrorNotification(err.message || 'Failed to send university for review')
      })
  }

  // Dropdown options
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear - 1950 + 1 }, (_, i) => (1950 + i).toString());

  return (
    <Box display="flex" p={2} sx={{ display: 'grid', gridTemplateColumns: '1.5fr 5fr', gap: '0.5rem', }}>
      <Box width="100%" >
        <Typography fontWeight="bold" mb={2}>Preferences</Typography>
        <Box borderRight="1px solid #ccc"
          sx={{ position: 'sticky', top: '9rem', display: 'block !important', height: 'calc(100vh - 10rem)', paddingLeft: '.25rem', marginLeft: '-.25rem', overflowY: 'auto', }}>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext
              items={fields}
              strategy={verticalListSortingStrategy}
            >
              {fields.map((field) => (
                <SortableItem
                  key={field.id}
                  id={field.id}
                  label={field.label}
                  visible={field.visible}
                  onToggle={() => toggleVisibility(field.id)}
                />
              ))}
            </SortableContext>
          </DndContext>
        </Box>
      </Box>

      <Box width="100%" pl={2} sx={{
        display: 'grid',
        gap: '1.5rem',
        gridTemplateAreas: `
          "intro toc"
          "content toc"
        `,
        gridTemplateColumns: '4fr 1fr',
        gridTemplateRows: 'auto 1fr',
      }}
      >
        <Box>
          <Typography fontWeight="bold" mb={2}>
            University Details
          </Typography>
          <Grid container spacing={2}>
            {fields.map((field) => {
              if (!field.visible) return null;

              if (field.id === 'yearEstablished') {
                return (
                  <Grid size={{ xs: 12, md: 6 }} key={field.id}>
                    <FormSelectAutocomplete
                      label={field.label}
                      value={field.value}
                      options={yearOptions}
                      fieldError={fieldError?.errors[field?.id]}
                      readOnly={field.readOnly || false}
                      onChange={(newValue) => {
                        setFields(fields.map(f =>
                          f.id === field.id ? { ...f, value: newValue } : f
                        ));
                      }}
                    />
                  </Grid>
                );
              }

              if (field.id === 'institutionType') {
                return (
                  <Grid size={{ xs: 12, md: 6 }} key={field.id}>
                    <FormSelectAutocomplete
                      label={field.label}
                      value={field.value}
                      options={institutionTypeOptions}
                      readOnly={field.readOnly || false}
                      fieldError={fieldError?.errors[field?.id]}
                      onChange={(newValue) => {
                        setFields(fields.map(f =>
                          f.id === field.id ? { ...f, value: newValue } : f
                        ));
                      }}
                    />
                  </Grid>
                );
              }

              if (field.type === 'upload') {
                return (
                  <Grid size={{ xs: 12, md: 12 }} key={field.id}>
                    <UniversityLogo
                      value={field.value}
                      readOnly={field.readOnly || false}
                      fieldError={fieldError?.errors[field?.id]}
                      onChange={(newValue) =>
                        setFields(
                          fields.map(f =>
                            f.id === field.id ? { ...f, value: newValue } : f
                          )
                        )
                      }
                    />
                  </Grid>
                );
              }

              if (field.type === 'campusImages') {
                return (
                  <Grid size={{ xs: 12, md: 12 }} key={field.id}>
                    <CampusImages
                      value={Array.isArray(field.value) ? field.value : []}
                      readOnly={field.readOnly || false}
                      fieldType={field.id}
                      fieldError={fieldError?.errors[field?.id]}
                      fieldErrors={fieldError?.errors}
                      onChange={(newValue) =>
                        setFields(fields.map(f =>
                          f.id === field.id ? { ...f, value: newValue } : f
                        ))
                      }
                    />
                  </Grid>
                );
              }

              if (field.type === 'accreditations') {
                return (
                  <Grid size={{ xs: 12, md: 12 }} key={field.id}>
                    <AccreditationForm
                      value={field.value}
                      readOnly={field.readOnly || false}
                      fieldError={fieldError?.errors[field?.id]}
                      onChange={(newValue) =>
                        setFields(
                          fields.map(f =>
                            f.id === field.id ? { ...f, value: newValue } : f
                          )
                        )
                      }
                    />
                  </Grid>
                );
              }

              if (['country', 'state', 'city'].includes(field.id)) {
                return (
                  <Grid size={{ xs: 12, md: 6 }} key={field.id}>
                    <LocationSelect
                      type={field.id}
                      field={field}
                      fields={fields}
                      setFields={setFields}
                      fieldError={fieldError?.errors[field?.id]}
                      countryId="country"
                      stateId="state"
                    />
                  </Grid>
                );
              }


              const contactFieldIds = [
                'postalCode',
                'contactEmail',
                'telephoneNumber',
                'studentSupportLine',
              ];

              const visibleContactFields = fields.filter(
                f => contactFieldIds.includes(f.id) && f.visible
              );

              const visibleCount = visibleContactFields.length;
              const mdSize = visibleCount > 0 ? Math.floor(12 / visibleCount) : 12;

              if (contactFieldIds.includes(field.id) && field.visible) {
                return (
                  <Grid size={{ xs: 12, md: 6, lg: 6, xl: mdSize }} key={field.id}>
                    <FormTextInput
                      label={field.label}
                      value={field.value}
                      placeholder={field.label}
                      readOnly={field.readOnly || false}
                      fieldError={fieldError?.errors[field?.id]}
                      onChange={(newValue) =>
                        setFields(
                          fields.map(f =>
                            f.id === field.id ? { ...f, value: newValue } : f
                          )
                        )
                      }
                    />
                  </Grid>
                );
              }


              if (field.type === 'socialMediaLinks') {
                return (
                  <Grid size={{ xs: 12, md: 12 }} key={field.id}>
                    <SocialMediaLinks
                      label={field.label}
                      value={field.value}
                      readOnly={field.readOnly || false}
                      fieldType={field.id}
                      fieldError={fieldError?.errors[field?.id]}
                      fieldErrors={fieldError?.errors}
                      onChange={(newValue) =>
                        setFields(
                          fields.map(f =>
                            f.id === field.id ? { ...f, value: newValue } : f
                          )
                        )
                      }
                    />
                  </Grid>
                );
              }

              if (field.type === 'universityOverview') {
                return (
                  <Grid size={{ xs: 12, md: 12 }} key={field.id}>
                    <TextEditor
                      label={field.label}
                      value={field.value}
                      readOnly={field.readOnly || false}
                      fieldError={fieldError?.errors[field?.id]}
                      onChange={(newValue) =>
                        setFields(
                          fields.map(f =>
                            f.id === field.id ? { ...f, value: newValue } : f
                          )
                        )
                      }
                    />
                  </Grid>
                );
              }

              if (field.type === 'reasonsToChooseUs' || field.type === 'advantage') {
                return (
                  <Grid size={{ xs: 12, md: 12 }} key={field.id}>
                    <ReasonsToChooseUs
                      label={field.label}
                      placeholder="Enter Description"
                      value={field.value}
                      readOnly={field.readOnly || false}
                      fieldType={field.id}
                      fieldError={fieldError?.errors[field?.id]}
                      fieldErrors={fieldError?.errors}
                      onChange={(newValue) =>
                        setFields(
                          fields.map(f =>
                            f.id === field.id ? { ...f, value: newValue } : f
                          )
                        )
                      }
                    />
                  </Grid>
                );
              }

              if (field.type === 'LeadershipandFacultyTeam') {
                return (
                  <Grid size={{ xs: 12, md: 12 }} key={field.id}>
                    <FacultyTeam
                      label={field.label}
                      value={field.value}
                      readOnly={field.readOnly || false}
                      fieldType={field.id}
                      fieldError={fieldError?.errors[field?.id]}
                      fieldErrors={fieldError?.errors}
                      onChange={(newValue) =>
                        setFields(
                          fields.map(f =>
                            f.id === field.id ? { ...f, value: newValue } : f
                          )
                        )
                      }
                    />
                  </Grid>
                );
              }

              if (field.type === 'studentSpotlight') {
                return (
                  <Grid size={{ xs: 12, md: 12 }} key={field.id}>
                    <StudentSpotlight
                      label={field.label}
                      value={field.value}
                      readOnly={field.readOnly || false}
                      fieldType={field.id}
                      fieldError={fieldError?.errors[field?.id]}
                      fieldErrors={fieldError?.errors}
                      onChange={(newValue) =>
                        setFields(
                          fields.map(f =>
                            f.id === field.id ? { ...f, value: newValue } : f
                          )
                        )
                      }
                    />
                  </Grid>
                );
              }

              if (field.type === 'onlineLearningResources') {
                return (
                  <Grid size={{ xs: 12, md: 12 }} key={field.id}>
                    <ReasonsToChooseUs
                      label={field.label}
                      placeholder="online learning resource"
                      value={field.value}
                      readOnly={field.readOnly || false}
                      fieldType={field.id}
                      fieldError={fieldError?.errors[field?.id]}
                      fieldErrors={fieldError?.errors}
                      onChange={newValue => {
                        setFields(
                          fields.map(f =>
                            f.id === field.id ? { ...f, value: newValue } : f
                          )
                        )
                      }}
                    />
                  </Grid>
                )
              }

              if (field.type === 'awardsandRecognitions') {
                return (
                  <Grid size={{ xs: 12, md: 12 }} key={field.id}>
                    <AwardsForm
                      label={field.label}
                      value={field.value || ""}
                      readOnly={field.readOnly || false}
                      fieldType={field.id}
                      fieldError={fieldError?.errors[field?.id]}
                      fieldErrors={fieldError?.errors}
                      onChange={(newValue) =>
                        setFields(
                          fields.map(f =>
                            f.id === field.id ? { ...f, value: newValue } : f
                          )
                        )
                      }
                    />
                  </Grid>
                );
              }

              // if (field.type === 'careerServicesandPlacementSupport') {
              //   return (
              //     <Grid size={{ xs: 12, md: 12 }} key={field.id}>
              //       <TextEditor
              //         label={field.label}
              //         value={field.value}
              //         readOnly={field.readOnly || false}
              //         onChange={(newValue) =>
              //           setFields(
              //             fields.map(f =>
              //               f.id === field.id ? { ...f, value: newValue } : f
              //             )
              //           )
              //         }
              //       />
              //     </Grid>
              //   );
              // }

              if (field.type === 'careerServicesandPlacementSupport') {
                //                 return (
                //   <Grid size={{ xs: 12, md: 12 }} key={field.id}>
                //     <ReasonsToChooseUs
                //       label={field.label}
                //       placeholder="For example, job roles, packages, industries, higher education path"
                //       value={field.value}
                //       readOnly={field.readOnly || false}
                //       onChange={newValue => {
                //         setFields(
                //           fields.map(f =>
                //             f.id === field.id ? { ...f, value: newValue } : f
                //           )
                //         )
                //       }}
                //     />
                //   </Grid>
                // )

                return <>
                  <Grid size={{ xs: 12, md: 12 }} key={field.id}>
                    {/* {field.readOnly ? <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(field.value) }} /> : */}
                      <QuillEditor
                      key={field.type}
                        label={field.label}
                        placeholder="For example, job roles, packages, industries, higher education path"
                        value={field.value || ""}
                        readOnly={field.readOnly || false}
                        onChange={newValue => {
                        if (universityId && fields?.find(ele => ele?.id === 'universityId')?.value !== universityId) return;

                        if (newValue !== field.value) {
                          setFields(fields.map(f =>
                            f.id === field.id ? { ...f, value: newValue } : f
                          ));
                        }
                      }}
                      fieldError={fieldError?.errors[field?.id]}
                      />
                  </Grid>
                </>
              }

              if (field.type === 'uploadBrochure') {
                return (
                  <Grid size={{ xs: 12, md: 12 }} key={field.id}>
                    <UploadBrochure
                      label={field.label}
                      value={field.value}
                      readOnly={field.readOnly || false}
                      onChange={(newValue) =>
                        setFields(
                          fields.map(f =>
                            f.id === field.id ? { ...f, value: newValue } : f
                          )
                        )
                      }
                    fieldError={fieldError?.errors[field?.id]}
                    />
                  </Grid>
                )
              }

              if (field.type === 'uploadRelevantDocument') {
                return (
                  <Grid size={{ xs: 12, md: 12 }} key={field.id}>
                    <UploadRelevantDocument
                      label={field.label}
                      value={field.value}
                      readOnly={field.readOnly || false}
                      fieldType={field.id}
                      fieldError={fieldError?.errors[field?.id]}
                      fieldErrors={fieldError?.errors}
                      onChange={newValue =>
                        setFieldState(
                          fields.map(f =>
                            f.id === field.id
                              ? { ...f, value: Array.isArray(newValue) ? newValue : [newValue] }
                              : f
                          )
                        )
                      }
                    />
                  </Grid>
                )
              }

              if (field.type === 'fullAddress' || field.type === 'universityOverview') {
                return (
                  <Grid size={{ xs: 12, md: 12 }} key={field.id}>
                    <TextEditor
                      label={field.label}
                      value={field.value}
                      readOnly={field.readOnly || false}
                      fieldType={field.id}
                      fieldError={fieldError?.errors[field?.id]}
                      fieldErrors={fieldError?.errors}
                      onChange={newValue =>
                        setFieldState(
                          fields.map(f =>
                            f.id === field.id ? { ...f, value: newValue } : f
                          )
                        )
                      }
                    />
                  </Grid>
                )
              }

              return (
                <Grid size={{ xs: 12, md: 12 }} key={field.id}>
                  <FormTextInput
                    label={field.label}
                    value={field.value}
                    placeholder={field.label}
                    readOnly={field.readOnly || false}
                    fieldType={field.id}
                    fieldError={fieldError?.errors[field?.id]}
                    fieldErrors={fieldError?.errors}
                    onChange={(newValue) => {
                      setFieldState(
                        fields.map(f =>
                          f.id === field.id ? { ...f, value: newValue } : f
                        )
                      )
                    }}
                  />
                </Grid>
              )
            })}

              <>
                <Box display="flex" gap={2} sx={{ justifyContent: 'end', mt: 3 }}>
                  {/* <Button sx={{ border: '1px solid #15243E', color: '#15243E' }}>
                    Preview
                  </Button> */}
                 {(!isReadOnly || editMode) && <Button
                    sx={{ border: '1px solid #1075FF', color: '#1075FF' }}
                    // onClick={handleUniversitySubmit}
                    onClick={()=>runValidation("submit")}
                    disabled={isReadOnly && !editMode}
                  >
                    Save
                  </Button>}
                  {!isReadOnly && <Button
                    sx={{ background: '#0463EF', color: '#fff' }}
                    onClick={handleUniversityApproval}
                    disabled={isReadOnly}
                  >
                    Send for Approval
                  </Button>}
                </Box>
              </>
          </Grid>
        </Box >
      </Box>
    </Box>
  )
}

export default UniversityDetails;